import React, { ReactNode, useCallback, useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { SidebarTemplate } from "./components/templates/SidebarTemplate";
import { LoginTemplate } from "./components/templates/LoginTemplate";
import { PageRoute } from "./constants/routes";
import { LoginPage } from "./components/pages/login";
import { ForgotPasswordPage } from "./components/pages/forgot_password";
import { ResetPasswordPage } from "./components/pages/reset_password";
import { DashboardPage } from "./components/pages/dashboard";
import { PropertyPage } from "./components/pages/property";
import { ClientPage } from "./components/pages/clients";
import { OwnerPage } from "./components/pages/owners";
import { FeaturePage } from "./components/pages/features";
import { PopularPlacePage } from "./components/pages/popular_place";
import { BlogPage } from "./components/pages/blogs";
import { HomePageImage } from "./components/pages/home_image";
import { MemberPage } from "./components/pages/members";
import { useDispatch, useSelector } from "react-redux";
import { appActions, appSelectors } from "./ducks/app";
import { PopupModal } from "./components/organisms/modal/PopupModal";
import { AppDispatch } from "./store";
import { LoadingIndicator } from "./components/atoms/LoadingIndicator";
import { AccountPage } from "./components/pages/account";
import { ForgotPasswordSentPage } from "./components/pages/forgot_password/ForgotPasswordSentPage";
import { authActions, authSelectors } from "./ducks/auth";
import { LocalStorageManager } from "./core/storage/LocalStorageManager";
import { AppConfig } from "./AppConfig";
import { ProtectedRoute } from "./components/atoms/ProtectedRoute";
import { Resource, Permission } from "./constants/permissions";
import { SessionExpiredModal } from "./components/organisms/modal/SessionExpiredModal";
import { confirmSessionExpired, setStore } from "./utils/auth";

export const RouteConfig: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const showModal = useSelector(appSelectors.showModal);
  const modalObject = useSelector(appSelectors.modalObject);
  const isLoading = useSelector(appSelectors.isLoading);
  const showSessionExpiredModal = useSelector(appSelectors.showSessionExpiredModal);

  // Auth state selectors
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const isInitialized = useSelector(authSelectors.isInitialized);

  // Initialize app on mount
  useEffect(() => {
    const accessToken = LocalStorageManager.Instance.getObject<string>(
      AppConfig.Instance.LocalStorageKey["accessToken"]
    );

    if (accessToken) {
      dispatch(authActions.setIsLoggedIn(true));
      dispatch(authActions.getUser.started({}));
    } else {
      // No token found, mark as initialized
      dispatch(authActions.setIsInitialized(true));
    }
  }, [dispatch]);

  // Set up store reference for auth utilities
  useEffect(() => {
    setStore(dispatch);
  }, [dispatch]);

  const _getRouteComponent = useCallback(
    (
      route: string,
      component: ReactNode,
      template: "login" | "sidebar",
      settings?: {}
    ) => {
      return (
        <Route
          path={route}
          element={
            <>
              {template === "login" && <LoginTemplate {...settings} />}
              {template === "sidebar" && <SidebarTemplate />}
            </>
          }
        >
          <Route index element={component} />
        </Route>
      );
    },
    []
  );

  // Component to handle default route based on auth state
  const DefaultRouteHandler = () => {
    // Show loading while authentication state is being determined
    if (!isInitialized) {
      return <LoadingIndicator isShown={true} />;
    }

    // Redirect based on authentication status
    if (isLoggedIn) {
      return <Navigate to={PageRoute.HOME} replace />;
    } else {
      return <Navigate to={PageRoute.LOGIN} replace />;
    }
  };

  return (
    <>
      <Routes>
        {/* Default route - redirect based on authentication status */}
        <Route path="/" element={<DefaultRouteHandler />} />
        {_getRouteComponent(PageRoute.LOGIN, <LoginPage />, "login")}
        {_getRouteComponent(
          PageRoute.FORGOT_PASSWORD,
          <ForgotPasswordPage />,
          "login"
        )}
        {/* Render the confirmation page directly, without LoginTemplate */}
        <Route path={PageRoute.FORGOT_PASSWORD_SENT} element={<ForgotPasswordSentPage />} />
        {_getRouteComponent(
          PageRoute.RESET_PASSWORD,
          <ResetPasswordPage />,
          "login"
        )}

        {_getRouteComponent(PageRoute.HOME,
          <ProtectedRoute resource={Resource.DASHBOARD} permission={Permission.READ}>
            <DashboardPage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(PageRoute.PROPERTIES,
          <ProtectedRoute resource={Resource.PROPERTIES} permission={Permission.READ}>
            <PropertyPage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(PageRoute.CLIENTS,
          <ProtectedRoute resource={Resource.CLIENTS} permission={Permission.READ}>
            <ClientPage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(PageRoute.OWNERS,
          <ProtectedRoute resource={Resource.OWNERS} permission={Permission.READ}>
            <OwnerPage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(PageRoute.FEATURES,
          <ProtectedRoute resource={Resource.FEATURES} permission={Permission.READ}>
            <FeaturePage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(
          PageRoute.POPULAR_PLACES,
          <ProtectedRoute resource={Resource.POPULAR_PLACES} permission={Permission.READ}>
            <PopularPlacePage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(PageRoute.BLOG,
          <ProtectedRoute resource={Resource.BLOGS} permission={Permission.READ}>
            <BlogPage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(
          PageRoute.HOMEPAGE_IMAGE,
          <ProtectedRoute resource={Resource.HOMEPAGE_IMAGES} permission={Permission.READ}>
            <HomePageImage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(PageRoute.MEMBERS,
          <ProtectedRoute resource={Resource.MEMBERS} permission={Permission.READ}>
            <MemberPage />
          </ProtectedRoute>,
          "sidebar"
        )}
        {_getRouteComponent(PageRoute.ACCOUNT, <AccountPage />, "sidebar")}
      </Routes>

      <PopupModal
        {...modalObject}
        isOpen={showModal}
        defaultCloseModal={() => {
          dispatch(appActions.hideModal());
          modalObject.onDefaultCloseModal?.();
        }}
      />

      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onConfirm={confirmSessionExpired}
      />

      <LoadingIndicator isShown={isLoading} />
    </>
  );
};
